import React from 'react';
import { useCampo } from './Field';

/**
 * Escolha curta em alvos grandes (at-58, frente C) — no SAAC, o **Sexo**.
 *
 * Substitui um `<select>` de 2 opções por dois botões lado a lado: no celular,
 * um select exige tocar, esperar a folha nativa e tocar de novo; aqui é um toque
 * num alvo de 44px+ (spec 01 §7). É o desenho do protótipo aprovado (D7).
 *
 * 🔴 **São `<input type="radio">` de verdade, apenas invisíveis.** Não é
 * detalhe de implementação:
 *   • **setas ← → ↑ ↓ navegam** entre as opções e `Tab` entra/sai do grupo — o
 *     navegador dá isso de graça para um grupo de radios com o mesmo `name`.
 *     Reimplementar com `div role="radio"` significaria escrever `onKeyDown`,
 *     `tabIndex` e `aria-checked` à mão, como a at-56 teve de fazer no `Tabs`
 *     (que não tinha alternativa nativa);
 *   • `required` e `:checked` continuam nativos;
 *   • formulário sem JS ainda envia o valor.
 *
 * ⚠️ **Nunca `display: none` nem `visibility: hidden`:** escondido de verdade, o
 * radio sai da ordem de foco e o grupo deixa de ser navegável pelo teclado.
 *
 * 🔴 **O input COBRE o label (`absolute inset-0 opacity-0`) — não use `sr-only`
 * aqui.** Bug real relatado por Victor em 09/09/2026: ao escolher Masculino ou
 * Feminino, aparecia **um retângulo branco no fim da tela**, com o conteúdo
 * cortado no meio.
 *
 * A causa é geométrica. `sr-only` é `position: absolute` com 1px e
 * `margin: -1px`; como o label **não** era `relative`, o containing block do
 * radio virava o **bloco inicial — o documento**, não a área do botão. Ao focá-lo
 * no clique, o navegador rola o que julgar necessário para revelá-lo, e aí o que
 * se deslocou foi o **documento inteiro**: o `html, body { overflow: hidden }` de
 * `tokens/base.css` impede o usuário de rolar, mas **não** impede a rolagem
 * programática — então o shell subiu, ficou preso lá, e o que apareceu embaixo do
 * `bg-shell` foi o branco do body.
 *
 * Cobrindo o label, o alvo do foco **é** a área do botão: `scrollIntoView` do
 * navegador não tem para onde rolar, e o defeito deixa de ser possível — em vez
 * de depender de um `position: relative` que alguém pode remover sem perceber.
 * ⚠️ Por isso o label é `tw-relative` e o input tem `tw-appearance-none`
 * (sem ele, com o `preflight` desligado, a bolinha nativa apareceria por cima do
 * rótulo). Coberto por teste em `ui-formulario.test.tsx`.
 *
 * ⚠️ **A opção marcada não se distingue só pela cor** — ela ganha borda e fundo,
 * e o texto está sempre visível (convenção do `Chip`: marcador tem texto, e o
 * consultor usa o app com sol na tela).
 */

export interface OpcaoRadio {
  valor: string;
  rotulo: string;
}

interface PropsRadioCards {
  /** `name` do grupo — é ele que faz as setas navegarem só entre estas opções. */
  nome: string;
  opcoes: readonly OpcaoRadio[];
  valor: string;
  aoMudar: (valor: string) => void;
  disabled?: boolean;
}

const RadioCards: React.FC<PropsRadioCards> = ({ nome, opcoes, valor, aoMudar, disabled = false }) => {
  const campo = useCampo();

  return (
    <div
      role="radiogroup"
      /* O rótulo do `Field` nomeia o grupo (ele usa `<span id>` quando
         `grupo`), e o erro/dica descrevem — sem isso o leitor de tela anuncia
         "grupo de opções" sem dizer de quê. */
      aria-labelledby={campo?.idRotulo}
      aria-describedby={campo?.descricao}
      className="tw-flex tw-gap-3"
    >
      {opcoes.map(({ valor: v, rotulo }) => {
        const marcada = valor === v;

        return (
          <label
            key={v}
            className={[
              /* `relative` é o containing block do input que cobre este label —
                 ver o aviso no topo do arquivo. */
              'tw-relative tw-flex tw-flex-1 tw-cursor-pointer tw-items-center tw-justify-center',
              'tw-rounded-xl tw-border tw-py-3 tw-text-sm tw-font-semibold tw-transition-colors',
              /* 44px de alvo de toque, como o `Botao` md. */
              'tw-min-h-[44px]',
              marcada
                ? 'tw-border-brand tw-bg-brand-tint tw-text-brand'
                : 'tw-border-superficie-borda tw-bg-white tw-text-texto-corpo hover:tw-bg-superficie',
              disabled ? 'tw-cursor-not-allowed tw-opacity-60' : '',
              /* O anel de foco vive no LABEL, porque o radio está `sr-only` —
                 sem isto, navegar por teclado não mostraria onde se está. */
              'focus-within:tw-ring-2 focus-within:tw-ring-brand/40',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <input
              type="radio"
              name={nome}
              value={v}
              checked={marcada}
              disabled={disabled}
              onChange={() => aoMudar(v)}
              /* Invisível, mas OCUPANDO o label inteiro: focável, navegável por
                 setas e sem deslocamento algum para o navegador rolar até. */
              className="tw-absolute tw-inset-0 tw-m-0 tw-h-full tw-w-full tw-cursor-pointer tw-appearance-none tw-opacity-0"
            />
            {rotulo}
          </label>
        );
      })}
    </div>
  );
};

export default RadioCards;
